import React, { useEffect, useMemo, useState } from 'react';
import { hasDashboardFullAccess } from '../../utils/workspaces';
import Select from 'react-select';
import Swal from 'sweetalert2';
import DashboardLayout from '../../components/DashboardLayout';
import axiosInstance from '../../utils/axiosInstance';
import { getImageURL } from '../../utils/api';
import AuthService from '../../services/authService';

const STATUS_OPTIONS = [
  { label: 'All statuses', value: 'all' },
  { label: 'Not started', value: 'not started' },
  { label: 'Ongoing', value: 'ongoing' },
  { label: 'Delivery completed', value: 'delivery completed' },
  { label: 'Batch completed', value: 'batch completed' },
];

const SUBMISSION_STATUS_OPTIONS = [
  'not submitted',
  'on-time submission',
  'late submission',
  're-submission',
];

const CODE_PRIORITY = {
  D: 1,
  M: 2,
  P: 3,
  SN: 4,
  R: 5,
  NS: 6,
};

const bandForMark = (value) => {
  if (!Number.isFinite(value)) return 'NS';
  if (value >= 70) return 'D';
  if (value >= 60) return 'M';
  if (value >= 40) return 'P';
  return 'R';
};

const normalizeMark = (mark) => {
  if (mark === null || mark === undefined || mark === '') return '';

  const numeric = Number(mark);
  if (Number.isFinite(numeric)) {
    return bandForMark(numeric);
  }

  const upper = String(mark).trim().toUpperCase();
  if (!upper) return '';

  if (upper === 'D' || upper.startsWith('DIST')) return 'D';
  if (upper === 'M' || upper.startsWith('MERIT')) return 'M';
  if (upper === 'P' || upper.startsWith('PASS')) return 'P';
  if (upper === 'R' || upper.startsWith('REF') || upper === 'F' || upper.startsWith('FAIL')) return 'R';

  return upper;
};

// Subject ordering helpers aligned with TopupExamBoard
function romanToInt(roman) {
  if (!roman) return null;
  const map = { I: 1, V: 5, X: 10, L: 50, C: 100, D: 500, M: 1000 };
  let total = 0;
  let prev = 0;
  const upper = roman.toUpperCase();
  for (let i = upper.length - 1; i >= 0; i -= 1) {
    const curr = map[upper[i]] || 0;
    if (curr < prev) total -= curr; else total += curr;
    prev = curr;
  }
  return total || null;
}

function buildSubjectOrderKey(subj) {
  const raw = (subj.shortName || subj.fullName || '').trim();
  if (!raw) return { base: '', num: null, display: '' };

  const normalized = raw.replace(/\s+/g, ' ').replace(/\s*-\s*/g, '-');
  const match = normalized.match(/^(.*?)[-\s]?([0-9]+|[IVXLCDM]+)$/i);

  if (!match) {
    return { base: normalized.toUpperCase(), num: null, display: normalized.toUpperCase() };
  }

  const base = (match[1] || '').trim().toUpperCase();
  const suffix = match[2] || '';
  const num = /^[0-9]+$/.test(suffix) ? Number(suffix) : romanToInt(suffix);
  return {
    base,
    num: Number.isNaN(num) ? null : num,
    display: normalized.toUpperCase(),
  };
}

function subjectPriority(subj) {
  const raw = (subj.shortName || subj.fullName || '').toLowerCase();
  const priorities = ['pro', 'int', 'viva', 'proc', 'fin'];

  const tokens = raw
    .replace(/\s*[-–—]\s*/g, ' ')
    .split(/[^a-z0-9]+/)
    .filter(Boolean);

  const last = tokens[tokens.length - 1] || '';
  const exactIdx = priorities.indexOf(last);
  if (exactIdx !== -1) return exactIdx;

  for (let i = 0; i < priorities.length; i += 1) {
    if (tokens.includes(priorities[i])) return i;
  }

  return null;
}

function orderSubjectsForCourse(subjects, courseTitle) {
  if (!Array.isArray(subjects) || !subjects.length) return subjects || [];

  return [...subjects].sort((a, b) => {
    const aSubj = {
      shortName: a.subjectCode,
      fullName: a.subjectName,
      semester: a.semester,
    };
    const bSubj = {
      shortName: b.subjectCode,
      fullName: b.subjectName,
      semester: b.semester,
    };

    const pa = subjectPriority(aSubj);
    const pb = subjectPriority(bSubj);
    if (pa !== null || pb !== null) {
      if (pa !== null && pb === null) return 1;
      if (pa === null && pb !== null) return -1;
      if (pa !== null && pb !== null && pa !== pb) return pa - pb;
    }

    const ka = buildSubjectOrderKey(aSubj);
    const kb = buildSubjectOrderKey(bSubj);

    if (ka.base !== kb.base) return ka.base.localeCompare(kb.base);
    if (ka.num !== null && kb.num !== null && ka.num !== kb.num) return ka.num - kb.num;

    const sa = parseInt(aSubj.semester, 10) || 0;
    const sb = parseInt(bSubj.semester, 10) || 0;
    if (sa !== sb) return sa - sb;

    return ka.display.localeCompare(kb.display);
  });
}

export default function TopupAssignmentTracking() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [batches, setBatches] = useState([]);
  const [status, setStatus] = useState('all');
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState(null);
  const [students, setStudents] = useState([]);
  const [studentSearch, setStudentSearch] = useState('');
  const [subjectCols, setSubjectCols] = useState([]);
  const [dbSubmissionCount, setDbSubmissionCount] = useState(null);
  const [loadingStudents, setLoadingStudents] = useState(false);
  const [studentError, setStudentError] = useState(null);
  const [editModal, setEditModal] = useState({
    open: false,
    student: null,
    subject: null,
    status: 'not submitted',
    marks: '',
    secondMarks: '',
    percentageRate: '',
  });
  const [savingSubmission, setSavingSubmission] = useState(false);
  const [showSheetMarks, setShowSheetMarks] = useState(false);
  const [creatingMissingSubmissions, setCreatingMissingSubmissions] = useState(false);
  const [elmsModuleCodes, setElmsModuleCodes] = useState([]);
  const [loadingElmsCodes, setLoadingElmsCodes] = useState(false);
  const [showElmsModal, setShowElmsModal] = useState(false);
  const [elmsSearch, setElmsSearch] = useState('');
  const user = AuthService.getUser();
  const isSuperAdmin = hasDashboardFullAccess(user) || user?.superAdmin?.staff?.type === 'admin';

  const fetchStudentsForSelected = async (options = {}) => {
    if (!selected) return;
    const { signal } = options;
    try {
      setLoadingStudents(true);
      setStudentError(null);
      const params = { batchIds: selected.value };
      const { data } = await axiosInstance.get('/admin/topup/assignment-tracking/students', { params, signal });

      const baseStudents = data.students || [];

      const rawSubjects = (data.subjects || []).map((s) => ({
        subjectId: s.subjectId,
        subjectCode: s.subjectCode,
        subjectName: s.subjectName,
        semester: s.semester,
        lecturerName: s.lecturerName || '',
        lecturerImagePath: s.lecturerImagePath || null,
        actualAssignmentSubmissionDate: s.actualAssignmentSubmissionDate || null,
      }));

      const courseTitle = baseStudents[0]?.courseName || baseStudents[0]?.courseShortName || '';
      const subjects = orderSubjectsForCourse(rawSubjects, courseTitle);

      const enrichedStudents = await enrichStudentsWithSubmissions(baseStudents, subjects);

      setSubjectCols(subjects);
      setStudents(enrichedStudents);
      setDbSubmissionCount(typeof data.submissionRowCount === 'number' ? data.submissionRowCount : null);
    } catch (e) {
      if (e.name !== 'CanceledError') {
        setStudentError(e?.response?.data?.message || e.message || 'Failed to load students');
        setSubjectCols([]);
        setStudents([]);
      }
    } finally {
      setLoadingStudents(false);
    }
  };

  const enrichStudentsWithSubmissions = async (baseStudents, subjects) => {
    if (!Array.isArray(baseStudents) || !baseStudents.length || !Array.isArray(subjects) || !subjects.length) {
      return baseStudents || [];
    }

    const codeByBatchSubjectId = new Map(
      subjects.map((s) => [Number(s.subjectId), s.subjectCode])
    );

    const enriched = await Promise.all(
      baseStudents.map(async (stu) => {
        const studentId = stu.studentId;
        if (!studentId) return { ...stu, marksBySubject: stu.marksBySubject || {}, statusBySubject: stu.statusBySubject || {}, feedbackBySubject: stu.feedbackBySubject || {} };

        try {
          const { data } = await axiosInstance.get(`/admin/students/${studentId}/submissions`);
          const submissions = Array.isArray(data?.submissions) ? data.submissions : [];
          // Preserve TOPUP tracking marks/status from the main endpoint;
          // only enrich with any available lecturer feedback from regular submissions.
          const marksBySubject = stu.marksBySubject || {};
          const statusBySubject = stu.statusBySubject || {};
          const feedbackBySubject = { ...(stu.feedbackBySubject || {}) };

          submissions.forEach((sub) => {
            const batchSubjectId = Number(sub.batch_subjectId ?? sub.batchSubjectId);
            if (!Number.isInteger(batchSubjectId)) return;
            const code = codeByBatchSubjectId.get(batchSubjectId);
            if (!code) return;
            if (sub.feedback !== null && sub.feedback !== undefined) {
              feedbackBySubject[code] = String(sub.feedback).trim();
            }
          });

          return {
            ...stu,
            marksBySubject,
            statusBySubject,
            feedbackBySubject,
          };
        } catch (e) {
          // If per-student submissions fail, keep the original student data.
          return { ...stu, marksBySubject: stu.marksBySubject || {}, statusBySubject: stu.statusBySubject || {}, feedbackBySubject: stu.feedbackBySubject || {} };
        }
      })
    );

    return enriched;
  };

  // Fetch ELMS module codes on page load
  useEffect(() => {
    const controller = new AbortController();
    const fetchElmsModuleCodes = async () => {
      try {
        setLoadingElmsCodes(true);
        const { data } = await axiosInstance.get('/admin/elms-module-codes/list', { signal: controller.signal });
        setElmsModuleCodes(Array.isArray(data) ? data : []);
        console.log('Loaded ELMS module codes (TOPUP):', data);
      } catch (e) {
        if (e.name !== 'CanceledError') {
          console.error('Failed to load ELMS module codes for TOPUP:', e);
        }
      } finally {
        setLoadingElmsCodes(false);
      }
    };
    fetchElmsModuleCodes();
    return () => controller.abort();
  }, []);

  const filteredElmsModules = useMemo(() => {
    if (!Array.isArray(elmsModuleCodes)) return [];
    const q = elmsSearch.trim().toLowerCase();
    if (!q) return elmsModuleCodes;
    return elmsModuleCodes.filter((row) => {
      const elms = String(row.elms_module_code || '').toLowerCase();
      const moduleCode = String(row.module_code || '').toLowerCase();
      const subjectFull = String(row.subject_full_name || row.subjectFullName || '').toLowerCase();
      const subjectShort = String(row.subject_short_name || row.subjectShortName || '').toLowerCase();
      return (
        elms.includes(q) ||
        moduleCode.includes(q) ||
        subjectFull.includes(q) ||
        subjectShort.includes(q)
      );
    });
  }, [elmsModuleCodes, elmsSearch]);

  // Fetch TOPUP batches (uses existing /admin/batches/topup endpoint)
  useEffect(() => {
    const controller = new AbortController();
    const run = async () => {
      try {
        setLoading(true);
        setError(null);
        const params = {};
        if (status && status !== 'all') params.status = status;
        if (search.trim()) params.search = search.trim();
        const { data } = await axiosInstance.get('/admin/batches/topup', { params, signal: controller.signal });
        setBatches(Array.isArray(data) ? data : []);
      } catch (e) {
        if (e.name !== 'CanceledError') {
          setError(e?.response?.data?.message || e.message || 'Failed to load batches');
        }
      } finally {
        setLoading(false);
      }
    };
    run();
    return () => controller.abort();
  }, [status, search]);

  const groupedOptions = useMemo(() => {
    const opts = batches.map((b) => ({
      value: b.batchId,
      label: `${b.batchName} - ${b.courseShortName || b.courseName || ''}${b.batchInternalName ? ` (IN: ${b.batchInternalName})` : ''}`,
      group: b.whatsappGroupName || 'No Group',
    }));
    const byGroup = {};
    opts.forEach((o) => {
      byGroup[o.group] = byGroup[o.group] || [];
      byGroup[o.group].push(o);
    });
    return Object.entries(byGroup)
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([label, options]) => ({ label, options: options.sort((x, y) => x.label.localeCompare(y.label)) }));
  }, [batches]);

  // Load students + subject columns for selected batch (expects backend to provide marks/status per subject)
  useEffect(() => {
    if (!selected) return;
    const controller = new AbortController();
    const run = async () => {
      await fetchStudentsForSelected({ signal: controller.signal });
    };
    run();
    return () => controller.abort();
  }, [selected]);

  const selectedBatchInfo = useMemo(() => {
    if (!selected) return null;
    const match = batches.find((b) => Number(b.batchId) === Number(selected.value));
    return match || null;
  }, [selected, batches]);

  const handleClearSelection = () => {
    setSelected(null);
    setStudents([]);
    setSubjectCols([]);
    setDbSubmissionCount(null);
  };

  const handleRemoveStudent = (studentId) => {
    Swal.fire({
      title: 'Remove student from view?',
      text: `Student ${studentId} will be hidden from this list (no data is modified).`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#dc2626',
      cancelButtonColor: '#6b7280',
      confirmButtonText: 'Hide',
    }).then((r) => {
      if (r.isConfirmed) {
        setStudents((prev) => prev.filter((s) => String(s.studentId) !== String(studentId)));
      }
    });
  };

  const expectedSubmissionCount = useMemo(() => {
    if (!students.length || !subjectCols.length) return 0;
    return students.length * subjectCols.length;
  }, [students, subjectCols]);

  const getSubmissionDisplay = (st, col, options = {}) => {
    const { useSheetMarks = false } = options;
    const code = col.subjectCode;
    const rawFirst = st?.marksBySubject?.[code];
    const rawSecond = st?.secondMarksBySubject?.[code];
    const status = st?.statusBySubject?.[code] ?? 'not submitted';
    const normalizedStatus = (status || '').trim().toLowerCase();

    const hasFirst = rawFirst !== null && rawFirst !== undefined && rawFirst !== '';
    const hasSecond = rawSecond !== null && rawSecond !== undefined && rawSecond !== '';

    const first = hasFirst ? Number(rawFirst) : null;
    const second = hasSecond ? Number(rawSecond) : null;

    const isNotSubmitted = status === 'not submitted';
    const isSubmittedNoMarks = !isNotSubmitted && !hasFirst && !hasSecond;

    let displayValue = null;
    let variant = 'not-submitted';
    let indicator = null;
    let label = 'NS';
    let band = 'NS';

    if (isNotSubmitted) {
      variant = 'not-submitted';
      label = 'NS';
      band = 'NS';
    } else if (isSubmittedNoMarks) {
      variant = 'submitted-no-marks';
      label = 'SN';
      band = 'SN';
    } else if (first !== null && !Number.isNaN(first) && first >= 40) {
      displayValue = first;
      variant = 'pass';
      indicator = '1';
      label = String(first);
      band = bandForMark(first);
    } else if (second !== null && !Number.isNaN(second) && second >= 40) {
      let effectiveSecond = second;
      if (useSheetMarks) {
        if (second > 43) {
          effectiveSecond = 43;
        } else if (second > 40) {
          effectiveSecond = 40;
        }
      }
      displayValue = effectiveSecond;
      variant = 'warning';
      indicator = '2';
      label = String(displayValue);
      band = bandForMark(effectiveSecond);
    } else if (first !== null && !Number.isNaN(first)) {
      displayValue = first;
      variant = 'danger';
      indicator = second !== null && !Number.isNaN(second) && second < 40 ? 'F' : 'R';
      label = String(first);
      band = 'R';
    } else if (second !== null && !Number.isNaN(second)) {
      displayValue = second;
      variant = 'danger';
      indicator = 'R';
      label = String(second);
      band = 'R';
    }

    if (useSheetMarks && normalizedStatus.includes('late')) {
      const numericDisplay = Number(displayValue);
      if (Number.isFinite(numericDisplay)) {
        let adjusted = numericDisplay;
        if (numericDisplay >= 43) {
          adjusted = 43;
        } else if (numericDisplay > 40) {
          adjusted = 40;
        }

        if (adjusted !== numericDisplay) {
          displayValue = adjusted;
          label = String(adjusted);
          band = bandForMark(adjusted);
        } else if (displayValue !== null) {
          label = String(displayValue);
        }
      }

      if (displayValue !== null || hasFirst || hasSecond) {
        variant = 'warning';
      }
    }

    const badgeClass = variant === 'pass'
      ? 'bg-emerald-100 text-emerald-800 border border-emerald-300'
      : variant === 'warning'
        ? 'bg-amber-100 text-amber-800 border border-amber-300'
        : variant === 'danger'
          ? 'bg-rose-100 text-rose-800 border border-rose-300'
          : variant === 'submitted-no-marks'
            ? 'bg-blue-100 text-blue-700 border border-blue-300'
            : 'bg-slate-100 text-slate-700 border border-slate-200';

    const indicatorClass = indicator === '1'
      ? 'bg-indigo-600 text-white'
      : indicator === '2'
        ? 'bg-amber-500 text-white'
        : 'bg-rose-600 text-white';

    return { displayValue, variant, indicator, label, band, badgeClass, indicatorClass };
  };

  const displayForSubject = (st, col) => getSubmissionDisplay(st, col).band;

  const formatDate = (value) => {
    if (!value) return '';
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return '';
    const day = String(d.getDate()).padStart(2, '0');
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const year = d.getFullYear();
    return `${day}/${month}/${year}`;
  };

  const chipClass = (status) => {
    const normalized = (status || '').toLowerCase();

    if (normalized === 'ontime') return 'bg-emerald-50 border-emerald-200 text-emerald-700';
    if (normalized === 'late') return 'bg-amber-50 border-amber-200 text-amber-800';
    if (normalized === 'r-sub') return 'bg-rose-50 border-rose-200 text-rose-700';

    return 'bg-slate-50 border-slate-200 text-slate-700';
  };

  const chipDotClass = (status) => {
    const normalized = (status || '').toLowerCase();

    if (normalized === 'ontime') return 'bg-emerald-500';
    if (normalized === 'late') return 'bg-amber-500';
    if (normalized === 'r-sub') return 'bg-rose-500';

    return 'bg-slate-400';
  };

  const formatShortSubmissionLabel = (code) => {
    const normalized = (code || '').toLowerCase();
    if (!normalized) return '';
    if (normalized === 'r-sub') return 'R-Sub';
    return code;
  };

  const getSubmissionStatusCounts = (student) => {
    const counts = { ontime: 0, late: 0, 'r-sub': 0 };
    if (!student || !subjectCols.length) return counts;

    subjectCols.forEach((col) => {
      const fullStatus = student?.statusBySubject?.[col.subjectCode] || '';
      const short = getShortSubmissionStatus(fullStatus);
      if (short && counts[short] !== undefined) {
        counts[short] += 1;
      }
    });

    return counts;
  };

  const getShortSubmissionStatus = (status) => {
    const normalized = (status || '').toLowerCase();

    if (normalized === 'on-time submission' || normalized === 'on time submission') return 'ontime';
    if (normalized === 'late submission') return 'late';
    if (normalized === 're-submission' || normalized === 'resubmission' || normalized === 're submission') return 'r-sub';

    return '';
  };

  const getAttendancePercent = (st) => {
    const raw = st?.attendancePercentage
      ?? st?.attendancePercent
      ?? st?.attendance_percentage
      ?? st?.attendance_percent
      ?? st?.totalAttendancePercentage
      ?? st?.attendance?.percentage
      ?? st?.attendance?.overall
      ?? st?.overallAttendance
      ?? st?.attendanceOverall;

    if (raw === null || raw === undefined) return null;

    const cleaned = typeof raw === 'string' ? raw.replace(/%/g, '').trim() : raw;
    const num = Number(cleaned);
    if (!Number.isFinite(num)) return null;

    const pct = num <= 1 ? num * 100 : num;
    const clamped = Math.max(0, Math.min(100, pct));
    return Math.round(clamped * 10) / 10;
  };

  const computeStudentStats = (st) => {
    const counts = { D: 0, M: 0, P: 0, SN: 0, R: 0, NS: 0 };
    // totalScore keeps legacy band-based ranking; totalMarks is numeric sum for ordering.
    let totalScore = 0;
    let bestRank = CODE_PRIORITY.NS;
    let totalMarks = 0;

    subjectCols.forEach((col) => {
      const { band } = getSubmissionDisplay(st, col);
      const normalizedCode = CODE_PRIORITY[band] ? band : 'NS';
      const rank = CODE_PRIORITY[normalizedCode];
      counts[normalizedCode] = (counts[normalizedCode] || 0) + 1;
      totalScore += rank;
      if (rank < bestRank) bestRank = rank;

      // Compute numeric contribution for ordering based on marks.
      const code = col.subjectCode;
      const rawFirst = st?.marksBySubject?.[code];
      const rawSecond = st?.secondMarksBySubject?.[code];

      const first = rawFirst !== null && rawFirst !== undefined && rawFirst !== ''
        ? Number(rawFirst)
        : null;
      const second = rawSecond !== null && rawSecond !== undefined && rawSecond !== ''
        ? Number(rawSecond)
        : null;

      let contribution = 0;

      if (first !== null && Number.isFinite(first) && first >= 40) {
        // First attempt pass: use full first mark.
        contribution = first;
      } else if (second !== null && Number.isFinite(second)) {
        // First attempt < 40: check second attempt.
        if (second > 43) {
          // Second attempt is capped at 43 for ordering.
          contribution = 43;
        } else {
          // Second attempt <= 43: use actual second mark.
          contribution = second;
        }
      } else if (first !== null && Number.isFinite(first)) {
        // No valid second attempt: use first mark even if < 40.
        contribution = first;
      }

      if (Number.isFinite(contribution) && contribution > 0) {
        totalMarks += contribution;
      }
    });

    return { totalScore, counts, bestRank, totalMarks };
  };

  const filteredStudents = useMemo(() => {
    const term = studentSearch.trim().toLowerCase();
    if (!term) return students;

    return students.filter((stu) => {
      const fields = [
        stu.fullName,
        stu.name,
        stu.studentId,
        stu.id,
        stu.registrationNumber,
        stu.student_registration_number,
        stu.telephone,
        stu.phone,
      ];

      return fields.some((field) => {
        if (field === null || field === undefined) return false;
        return String(field).toLowerCase().includes(term);
      });
    });
  }, [students, studentSearch]);

  const sortedStudents = useMemo(() => {
    if (!filteredStudents.length) return [];
    if (!subjectCols.length) return [...filteredStudents];

    const statsList = filteredStudents.map((student) => ({ student, stats: computeStudentStats(student) }));

    statsList.sort((a, b) => {
      const sa = a.stats;
      const sb = b.stats;

      // Primary order: total numeric marks (highest to lowest).
      if (sa.totalMarks !== sb.totalMarks) return sb.totalMarks - sa.totalMarks;

      // Fallback: preserve legacy band-based ordering for ties.
      if (sa.totalScore !== sb.totalScore) return sa.totalScore - sb.totalScore;
      if (sa.counts.D !== sb.counts.D) return sb.counts.D - sa.counts.D;
      if (sa.counts.M !== sb.counts.M) return sb.counts.M - sa.counts.M;
      if (sa.counts.P !== sb.counts.P) return sb.counts.P - sa.counts.P;
      if (sa.counts.SN !== sb.counts.SN) return sa.counts.SN - sb.counts.SN;
      if (sa.counts.R !== sb.counts.R) return sa.counts.R - sb.counts.R;
      if (sa.counts.NS !== sb.counts.NS) return sa.counts.NS - sb.counts.NS;
      if (sa.bestRank !== sb.bestRank) return sa.bestRank - sb.bestRank;
      return (a.student.fullName || '').localeCompare(b.student.fullName || '');
    });

    return statsList.map((entry) => entry.student);
  }, [filteredStudents, subjectCols]);

  const openSubmissionModal = (student, subject) => {
    if (!student || !subject) return;
    const currentStatus = student?.statusBySubject?.[subject.subjectCode] || 'not submitted';
    const currentMarks = student?.marksBySubject?.[subject.subjectCode] ?? '';
    const currentSecond = student?.secondMarksBySubject?.[subject.subjectCode] ?? '';
    const currentPercentage = student?.percentageBySubject?.[subject.subjectCode] ?? '';
    setEditModal({
      open: true,
      student,
      subject,
      status: currentStatus,
      marks: currentMarks ?? '',
      secondMarks: currentSecond ?? '',
      percentageRate: currentPercentage ?? '',
    });
  };

  const closeSubmissionModal = () => {
    setEditModal({ open: false, student: null, subject: null, status: 'not submitted', marks: '', secondMarks: '', percentageRate: '' });
  };

  const handleSaveSubmission = async () => {
    if (!editModal.student || !editModal.subject) return;
    try {
      setSavingSubmission(true);
      const parsedMarks = editModal.marks === '' || editModal.marks === null || editModal.marks === undefined
        ? null
        : Number(editModal.marks);

      const parsedSecond = editModal.secondMarks === '' || editModal.secondMarks === null || editModal.secondMarks === undefined
        ? null
        : Number(editModal.secondMarks);

      const parsedPercentage = editModal.percentageRate === '' || editModal.percentageRate === null || editModal.percentageRate === undefined
        ? null
        : Number(editModal.percentageRate);

      const payload = {
        studentId: editModal.student.studentId,
        batchSubjectId: editModal.subject.subjectId,
        submission_status: editModal.status,
        marks: Number.isFinite(parsedMarks) ? parsedMarks : null,
        second_marks: Number.isFinite(parsedSecond) ? parsedSecond : null,
        percentage_rate: Number.isFinite(parsedPercentage) ? parsedPercentage : null,
      };
      await axiosInstance.put('/admin/topup/assignment-tracking/submission', payload);

      setStudents((prev) => prev.map((s) => {
        if (String(s.studentId) !== String(editModal.student.studentId)) return s;
        const nextMarks = { ...(s.marksBySubject || {}) };
        const nextSecond = { ...(s.secondMarksBySubject || {}) };
        const nextStatus = { ...(s.statusBySubject || {}) };
        const nextPercentage = { ...(s.percentageBySubject || {}) };

        const markValue = Number.isFinite(parsedMarks) ? parsedMarks : '';

        if (markValue === '' || markValue === null || markValue === undefined) {
          delete nextMarks[editModal.subject.subjectCode];
        } else {
          nextMarks[editModal.subject.subjectCode] = markValue;
        }

        const secondValue = Number.isFinite(parsedSecond) ? parsedSecond : '';

        if (secondValue === '' || secondValue === null || secondValue === undefined) {
          delete nextSecond[editModal.subject.subjectCode];
        } else {
          nextSecond[editModal.subject.subjectCode] = secondValue;
        }

        const percentageValue = Number.isFinite(parsedPercentage) ? parsedPercentage : '';

        if (percentageValue === '' || percentageValue === null || percentageValue === undefined) {
          delete nextPercentage[editModal.subject.subjectCode];
        } else {
          nextPercentage[editModal.subject.subjectCode] = percentageValue;
        }

        if (!editModal.status) {
          delete nextStatus[editModal.subject.subjectCode];
        } else {
          nextStatus[editModal.subject.subjectCode] = editModal.status;
        }

        return { ...s, marksBySubject: nextMarks, secondMarksBySubject: nextSecond, statusBySubject: nextStatus, percentageBySubject: nextPercentage };
      }));

      Swal.fire('Saved', 'Assignment submission updated.', 'success');
      closeSubmissionModal();
    } catch (err) {
      const message = err?.response?.data?.message || err.message || 'Failed to save submission.';
      Swal.fire('Error', message, 'error');
    } finally {
      setSavingSubmission(false);
    }
  };

  const handleDownloadExcel = async () => {
    if (!selected) {
      Swal.fire('No Batch', 'Please select a batch first.', 'warning');
      return;
    }

    if (!students.length || !subjectCols.length) {
      Swal.fire('No Data', 'No students or subjects to export for this batch.', 'info');
      return;
    }

    try {
      const XLSX = await import('xlsx');

      const header = ['Student ID', 'Student Name'];
      subjectCols.forEach((col) => {
        header.push(col.subjectCode || col.subjectName || '');
      });

      const rows = students.map((st) => {
        const row = [
          st.studentId || '',
          st.fullName || st.name || '',
        ];

        subjectCols.forEach((col) => {
          const cell = getSubmissionDisplay(st, col, { useSheetMarks: showSheetMarks });
          const status = st?.statusBySubject?.[col.subjectCode] || '';
          const shortStatus = getShortSubmissionStatus(status);
          let mark = '';

          if (cell.displayValue !== null && cell.displayValue !== undefined && cell.displayValue !== '') {
            const numericValue = Number(cell.displayValue);
            if (Number.isFinite(numericValue)) {
              mark = numericValue;
            }
          }

          if (cell.indicator === '2' && mark !== '' && mark !== null && mark !== undefined) {
            mark = `${mark}^`;
          }

          if (shortStatus === 'r-sub' && mark !== '' && mark !== null && mark !== undefined) {
            mark = `${mark}*`;
          }

          row.push(mark);
        });

        return row;
      });

      const worksheetData = [header, ...rows];
      const worksheet = XLSX.utils.aoa_to_sheet(worksheetData);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Topup Marks');

      const wbout = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
      const blob = new Blob([wbout], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');

      const fileNameBatch = (selected.label || 'batch').replace(/[^a-z0-9]+/gi, '_');
      a.href = url;
      a.download = `topup_marks_${fileNameBatch}.xlsx`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      const message = err?.message || 'Failed to generate Excel sheet.';
      Swal.fire('Error', message, 'error');
    }
  };

  const handleCreateMissingSubmissions = async () => {
    if (!selected) {
      Swal.fire('No Batch', 'Please select a batch first.', 'warning');
      return;
    }

    if (!students.length || !subjectCols.length) {
      Swal.fire('No Data', 'No students or subjects found for this batch.', 'info');
      return;
    }

    const expected = expectedSubmissionCount;
    const existing = dbSubmissionCount ?? 0;
    if (expected <= 0 || existing >= expected) {
      Swal.fire('Nothing To Create', 'All expected TOPUP submission rows already exist.', 'info');
      return;
    }

    const result = await Swal.fire({
      title: 'Create missing submission rows?',
      text: `This will insert ${expected - existing} missing TOPUP submission row(s) with status "not submitted".`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Yes, create',
      cancelButtonText: 'Cancel',
      confirmButtonColor: '#16a34a',
    });

    if (!result.isConfirmed) return;

    try {
      setCreatingMissingSubmissions(true);
      await axiosInstance.post('/admin/topup/assignment-tracking/ensure-submissions', {
        batchId: selected.value,
      });

      await fetchStudentsForSelected();

      Swal.fire('Done', 'Missing TOPUP submission rows have been created.', 'success');
    } catch (err) {
      const message = err?.response?.data?.message || err.message || 'Failed to create missing submission rows.';
      Swal.fire('Error', message, 'error');
    } finally {
      setCreatingMissingSubmissions(false);
    }
  };

  return (
    <DashboardLayout>
      <div className="p-6 space-y-6">
        <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-4 space-y-4">
          <div className="grid md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Search</label>
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500"
                placeholder="Search by batch, internal or WhatsApp group name"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
              <select
                value={status}
                onChange={(e) => setStatus(e.target.value)}
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500"
              >
                {STATUS_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Quick Actions</label>
              <button
                type="button"
                className="w-full inline-flex items-center justify-center gap-2 rounded-md bg-gradient-to-r from-indigo-600 to-blue-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:from-indigo-700 hover:to-blue-700 transition-all"
                onClick={() => setShowElmsModal(true)}
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                Show ELMS Modules
              </button>
            </div>
          </div>

          <div className="pt-2">
            <label className="block text-sm font-medium text-gray-700 mb-2">Assign TOPUP Batches by WhatsApp Group</label>
            <Select
              isLoading={loading}
              options={groupedOptions}
              value={selected}
              onChange={(val) => setSelected(val || null)}
              classNamePrefix="react-select"
              placeholder="Select a batch (grouped by WhatsApp group)..."
              styles={{ menu: (base) => ({ ...base, zIndex: 40 }) }}
              isClearable
            />
            {error && (
              <p className="mt-2 text-sm text-red-600">{error}</p>
            )}
            {!loading && batches.length === 0 && !error && (
              <p className="mt-2 text-sm text-gray-600">No TOPUP batches found.</p>
            )}
          </div>

          <div className="pt-4 border-t">
            <div className="flex flex-wrap items-center gap-3 text-sm">
              <span className="inline-flex items-center rounded-md bg-indigo-50 px-2.5 py-1.5 font-medium text-indigo-700 border border-indigo-200">
                Selected: {selected ? 1 : 0}
              </span>
              <span className="text-gray-400">|</span>
              <span className="text-gray-700">Group: {selected ? selected.group : '-'}</span>
            </div>

            {selected && (
              <div className="mt-4 overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200 text-sm">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-3 py-2 text-left font-semibold text-gray-700">WhatsApp Group</th>
                      <th className="px-3 py-2 text-left font-semibold text-gray-700">Batch</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-100">
                    <tr key={selected.value}>
                      <td className="px-3 py-2 text-gray-800">{selected.group}</td>
                      <td className="px-3 py-2 text-gray-800">{selected.label}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            )}

            <div className="mt-4 flex gap-2">
              <button
                type="button"
                className="inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50"
                onClick={handleClearSelection}
              >
                Clear
              </button>
            </div>
          </div>
        </div>

        {showElmsModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center px-4 py-4">
            <div
              className="fixed inset-0 bg-gray-900 bg-opacity-75 transition-opacity"
              aria-hidden="true"
              onClick={() => { setShowElmsModal(false); setElmsSearch(''); }}
            ></div>
            <div className="relative z-50 w-full max-w-5xl transform overflow-hidden rounded-lg bg-white shadow-xl">
              <div className="border-b border-gray-200 bg-gradient-to-r from-indigo-50 to-blue-50 px-6 py-4 flex items-center justify-between">
                <div>
                  <h2 className="text-lg font-semibold text-gray-900">ELMS Modules</h2>
                  <p className="text-xs text-gray-600">Search ELMS module mappings by ELMS code, module code, or subject.</p>
                </div>
                <button
                  onClick={() => { setShowElmsModal(false); setElmsSearch(''); }}
                  className="rounded-md text-gray-400 hover:text-gray-600 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                >
                  <span className="sr-only">Close</span>
                  <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              <div className="px-6 py-4 space-y-4">
                <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                  <div className="relative w-full sm:max-w-md">
                    <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                      <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                      </svg>
                    </div>
                    <input
                      type="text"
                      value={elmsSearch}
                      onChange={(e) => setElmsSearch(e.target.value)}
                      placeholder="Search by ELMS/module/subject..."
                      className="block w-full rounded-md border border-gray-300 bg-white py-2 pl-10 pr-3 text-sm placeholder-gray-400 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                    />
                  </div>
                  {loadingElmsCodes && (
                    <div className="flex items-center gap-2 text-xs text-gray-500">
                      <span className="inline-block h-3 w-3 animate-spin rounded-full border-2 border-indigo-500 border-t-transparent" />
                      Loading ELMS modules...
                    </div>
                  )}
                </div>

                <div className="max-h-[60vh] overflow-y-auto rounded-lg border border-gray-200">
                  <table className="min-w-full divide-y divide-gray-200 text-sm">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-3 py-2 text-left text-xs font-medium uppercase tracking-wider text-gray-500">#</th>
                        <th className="px-3 py-2 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Subject</th>
                        <th className="px-3 py-2 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Module Code</th>
                        <th className="px-3 py-2 text-left text-xs font-medium uppercase tracking-wider text-gray-500">ELMS Module Code</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200 bg-white">
                      {filteredElmsModules.length ? (
                        filteredElmsModules.map((row, idx) => (
                          <tr key={row.elms_module_code_id || `${row.elms_module_code || ''}-${idx}`} className="hover:bg-gray-50">
                            <td className="px-3 py-2 align-top text-xs text-gray-500">{idx + 1}</td>
                            <td className="px-3 py-2 align-top">
                              <div className="text-sm font-medium text-gray-900">{row.subject_full_name || row.subjectFullName || '-'}</div>
                              <div className="text-xs text-gray-500">{row.subject_short_name || row.subjectShortName || ''}</div>
                            </td>
                            <td className="px-3 py-2 align-top text-sm font-medium text-gray-800">{row.module_code || '-'}</td>
                            <td className="px-3 py-2 align-top text-sm text-gray-800">{row.elms_module_code || '-'}</td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td className="px-3 py-8 text-center text-sm text-gray-500" colSpan={4}>
                            {loadingElmsCodes ? 'Loading ELMS modules...' : (elmsSearch ? 'No modules match your search.' : 'No ELMS modules found.')}
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>

                <div className="flex justify-end border-t border-gray-100 pt-3 mt-2">
                  <button
                    type="button"
                    className="inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
                    onClick={() => { setShowElmsModal(false); setElmsSearch(''); }}
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {selected && (
          <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-4 space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Selected batch</p>
                <p className="text-lg font-semibold text-gray-900">{selected.label}</p>
              </div>
              {selectedBatchInfo && (
                <div className="text-sm text-gray-600 text-right">
                  <div>Status: {selectedBatchInfo.status || 'N/A'}</div>
                  <div>WhatsApp: {selectedBatchInfo.whatsappGroupName || 'N/A'}</div>
                  {selectedBatchInfo.batchStartingDate && <div>Start: {selectedBatchInfo.batchStartingDate}</div>}
                </div>
              )}
            </div>

            <div className="border-t pt-4 space-y-3">
              <div className="flex items-center flex-wrap gap-3">
                <h2 className="text-lg font-semibold text-gray-900">Students</h2>
                <div className="flex items-center gap-2 text-sm">
                  <span className="inline-flex items-center rounded-md bg-gray-100 px-2 py-1 text-gray-800 border border-gray-200" title="Expected submissions = students × subjects">
                    Expected: {expectedSubmissionCount}
                  </span>
                  <span className="inline-flex items-center rounded-md bg-gray-100 px-2 py-1 text-gray-800 border border-gray-200" title="Existing submission records found">
                    Existing: {dbSubmissionCount ?? '—'}
                  </span>
                  {dbSubmissionCount !== null && expectedSubmissionCount > 0 && dbSubmissionCount >= expectedSubmissionCount && (
                    <span className="inline-flex items-center rounded-md bg-emerald-100 px-2 py-1 text-emerald-800 border border-emerald-200">All good</span>
                  )}
                  {dbSubmissionCount !== null && expectedSubmissionCount > 0 && dbSubmissionCount < expectedSubmissionCount && (
                    <button
                      type="button"
                      onClick={handleCreateMissingSubmissions}
                      disabled={creatingMissingSubmissions || loadingStudents}
                      className="inline-flex items-center gap-1 rounded-md bg-amber-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-amber-700 border border-amber-700 shadow-sm disabled:opacity-60 disabled:cursor-not-allowed"
                    >
                      {creatingMissingSubmissions ? 'Creating…' : 'Create submission detail'}
                    </button>
                  )}
                </div>
                  <div className="flex items-center gap-4 ml-auto">
                    <label className="inline-flex items-center gap-2 text-xs font-medium text-gray-700">
                      <input
                        type="checkbox"
                        className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                        checked={showSheetMarks}
                        onChange={(e) => setShowSheetMarks(e.target.checked)}
                      />
                      <span>Show Sheet Marks (cap 2nd to 43)</span>
                    </label>
                  {loadingStudents && <span className="text-sm text-gray-500">Loading...</span>}
                  <button
                    type="button"
                      className="inline-flex items-center gap-2 rounded-md bg-emerald-600 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-700 shadow-sm transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                      onClick={handleDownloadExcel}
                      disabled={!selected || loadingStudents || !students.length}
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 16v-8m0 8l-3-3m3 3l3-3M5 20h14a2 2 0 002-2V6a2 2 0 00-2-2H9.414a1 1 0 00-.707.293L5 7.999V18a2 2 0 002 2z" />
                    </svg>
                      Generate Excel Sheet
                  </button>
                </div>
              </div>

              <div className="w-full mt-3">
                <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="student-search">Student search</label>
                <div className="relative">
                  <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                    <svg className="h-4 w-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-4.35-4.35M17 10a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                  </div>
                  <input
                    id="student-search"
                    type="text"
                    value={studentSearch}
                    onChange={(e) => setStudentSearch(e.target.value)}
                    className="w-full rounded-md border border-gray-300 bg-white py-2 pl-9 pr-3 text-sm placeholder-gray-400 focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500"
                    placeholder="Search by name, student ID, registration #, or telephone"
                  />
                </div>
                <p className="mt-1 text-xs text-gray-500">Filter by any student identifier or phone number.</p>
              </div>

              {studentError && <div className="text-sm text-red-600">{studentError}</div>}
              {!loadingStudents && students.length === 0 && (
                <div className="text-sm text-gray-600">No students found for this batch.</div>
              )}
              {students.length > 0 && !loadingStudents && filteredStudents.length === 0 && studentSearch.trim() && (
                <div className="text-sm text-gray-600">No students match your search.</div>
              )}

              {sortedStudents.length > 0 && (
                <div className="overflow-x-auto">
                  <table className="min-w-full text-sm text-left">
                    <thead>
                      <tr className="bg-gray-50 text-gray-700 border-b">
                        <th
                          className="px-3 py-2 font-medium text-center sticky left-0 z-20 bg-white"
                          style={{ width: 40 }}
                        >
                          #
                        </th>
                        <th
                          className="px-3 py-2 font-medium sticky left-14 z-20 bg-white"
                          style={{ width: 90, left: 40 }}
                        >
                          Image
                        </th>
                        <th
                          className="px-3 py-2 font-medium sticky left-40 z-20 bg-white"
                          style={{ minWidth: 220, left: 130 }}
                        >
                          Identity
                        </th>
                        {subjectCols.map((col) => (
                          <th
                            key={col.subjectId}
                            className={`px-3 py-2 font-semibold text-center whitespace-nowrap align-top ${col.actualAssignmentSubmissionDate ? 'bg-emerald-50 text-emerald-800' : 'bg-gray-50 text-gray-700'}`}
                          >
                            <div className="flex flex-col items-center gap-1">
                              {col.lecturerImagePath && (
                                <img
                                  src={getImageURL(col.lecturerImagePath)}
                                  alt={col.lecturerName || 'Lecturer'}
                                  className="h-8 w-8 rounded-full object-cover border border-emerald-200"
                                />
                              )}
                              <div className="text-sm font-bold">{col.subjectCode}</div>
                              {col.lecturerName && (
                                <div className="text-[11px] text-emerald-800 truncate max-w-[120px]" title={col.lecturerName}>
                                  {col.lecturerName}
                                </div>
                              )}
                              {col.actualAssignmentSubmissionDate && (
                                <div className="text-[11px] text-emerald-700">{formatDate(col.actualAssignmentSubmissionDate)}</div>
                              )}
                            </div>
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {sortedStudents.map((s, idx) => {
                        const stats = computeStudentStats(s);
                        const attendancePct = getAttendancePercent(s);
                        const submissionCounts = getSubmissionStatusCounts(s);
                        return (
                          <tr key={`${s.studentId}-${idx}`} className="align-top">
                            <td className="px-3 py-3 text-center text-gray-800 sticky left-0 z-10 bg-white">{idx + 1}</td>
                            <td className="px-3 py-3 sticky left-14 z-10 bg-white" style={{ left: 40 }}>
                              <div className="flex flex-col items-center gap-2">
                                {s.imageUrl || s.imagePath ? (
                                  <img
                                    src={getImageURL(s.imagePath)}
                                    alt={s.fullName || 'student'}
                                    className="h-35 w-20 rounded-md object-cover border"
                                  />
                                ) : (
                                  <div className="h-16 w-16 rounded-md bg-gray-100 border flex items-center justify-center text-gray-500 text-xs">No photo</div>
                                )}
                                {isSuperAdmin && (
                                  <button
                                    type="button"
                                    onClick={() => handleRemoveStudent(s.studentId)}
                                    className="inline-flex items-center rounded-md border border-rose-200 bg-rose-50 px-2.5 py-1 text-xs font-medium text-rose-700 hover:bg-rose-100"
                                  >
                                    Clear marks
                                  </button>
                                )}
                              </div>
                            </td>
                            <td className="px-3 py-3 text-gray-800 sticky left-40 z-10 bg-white" style={{ left: 130 }}>
                              <div className="font-semibold text-base">{s.fullName || s.name || 'Unknown'}</div>
                              <div className="text-xs text-gray-600">ID: {s.studentId || s.id || '—'}</div>
                              <div className="text-xs text-gray-600">Tel: {s.telephone || s.phone || '—'}</div>
                              <div className="text-xs text-gray-600">Reg #: {s.registrationNumber || s.student_registration_number || '—'}</div>
                              <div className="mt-1">{s.registrationStatus && (
                                  <span className="inline-flex items-center rounded-md bg-green-100 px-1.5 py-0.5 text-[10px] font-semibold text-green-700 border border-green-200">
                                    {s.registrationStatus}
                                  </span>
                                )}</div>
                              <div className="mt-3 space-y-1">
                                <div className="h-px bg-gray-200" />
                                <div className="flex items-center justify-between text-[11px] text-gray-600">
                                  <span>Total attendance</span>
                                  <span className="font-semibold text-gray-800">{attendancePct ?? 'N/A'}{attendancePct !== null ? '%' : ''}</span>
                                </div>
                                <div className="h-2 w-full rounded-full bg-gray-100 border border-gray-200 overflow-hidden">
                                  <div
                                    className="h-full transition-all"
                                    style={{
                                      width: `${attendancePct ?? 0}%`,
                                      opacity: attendancePct !== null ? 1 : 0.35,
                                      backgroundColor:
                                        attendancePct === null ? '#cbd5e1' :
                                        attendancePct >= 80 ? '#22c55e' :
                                        attendancePct >= 60 ? '#f59e0b' :
                                        '#ef4444',
                                    }}
                                  />
                                </div>
                              </div>
                              <div className="mt-3 flex flex-wrap gap-2 text-[10px]">
                                <span className="inline-flex items-center gap-1 rounded-md border border-emerald-200 bg-emerald-50 px-2 py-0.5 font-semibold text-emerald-700">
                                  <span className="text-[9px] tracking-wide font-bold">OS</span>
                                  <span className="min-w-[1.25rem] text-center text-xs font-bold font-mono leading-tight">{submissionCounts.ontime}</span>
                                </span>
                                <span className="inline-flex items-center gap-1 rounded-md border border-amber-200 bg-amber-50 px-2 py-0.5 font-semibold text-amber-700">
                                  <span className="text-[9px] tracking-wide font-bold">LS</span>
                                  <span className="min-w-[1.25rem] text-center text-xs font-bold font-mono leading-tight">{submissionCounts.late}</span>
                                </span>
                                <span className="inline-flex items-center gap-1 rounded-md border border-rose-200 bg-rose-50 px-2 py-0.5 font-semibold text-rose-700">
                                  <span className="text-[9px] tracking-wide font-bold">RS</span>
                                  <span className="min-w-[1.25rem] text-center text-xs font-bold font-mono leading-tight">{submissionCounts['r-sub']}</span>
                                </span>
                              </div>
                            </td>
                            {subjectCols.map((col) => {
                              const cell = getSubmissionDisplay(s, col, { useSheetMarks: showSheetMarks });
                              const fullStatus = s.statusBySubject?.[col.subjectCode] || 'not submitted';
                              const shortStatus = getShortSubmissionStatus(fullStatus);
                              return (
                                <td
                                  key={`${s.studentId}-${col.subjectId}`}
                                  className="px-2 py-3 text-center align-middle cursor-pointer hover:bg-indigo-50 transition-colors"
                                  title="Double-click to edit submission"
                                  onDoubleClick={() => openSubmissionModal(s, col)}
                                >
                                  <div className="flex flex-col items-center gap-1">
                                    <span className="relative inline-flex items-center">
                                      <span
                                        className={`inline-flex h-8 w-12 items-center justify-center rounded ${cell.badgeClass} text-xs font-semibold`}
                                        title={`${col.subjectCode} • ${fullStatus}`}
                                      >
                                        {cell.variant === 'not-submitted' ? 'NS' : cell.variant === 'submitted-no-marks' ? 'SN' : (cell.label || '-')}
                                      </span>
                                      {cell.indicator && cell.variant !== 'not-submitted' && cell.variant !== 'submitted-no-marks' && (
                                        <span
                                          className={`-ml-1 inline-flex items-center justify-center h-4 w-4 text-[10px] font-bold rounded-full ${cell.indicatorClass} border border-white shadow`}
                                          title={cell.indicator === 'F' ? 'Failed (both attempts < 40)' : (cell.indicator === 'R' ? 'Below pass mark' : (cell.indicator === '1' ? 'First attempt' : 'Second attempt'))}
                                          style={{ transform: 'translateY(-6px)' }}
                                        >
                                          {cell.indicator}
                                        </span>
                                      )}
                                    </span>
                                    {shortStatus && (
                                      <span
                                        className={`inline-flex items-center gap-1 rounded-md border px-2 py-0.5 text-[10px] font-semibold tracking-wide shadow-sm ${chipClass(shortStatus)}`}
                                      >
                                        <span className={`h-1.5 w-1.5 rounded-full ${chipDotClass(shortStatus)}`} />
                                        {formatShortSubmissionLabel(shortStatus)}
                                      </span>
                                    )}
                                  </div>
                                </td>
                              );
                            })}
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}

      {editModal.open && (
        <div className="fixed inset-0 z-[1200] flex items-center justify-center bg-black/50 px-4">
          <div className="w-full max-w-md rounded-lg bg-white shadow-2xl border border-gray-200">
            <div className="flex items-center justify-between border-b border-gray-200 px-4 py-3">
              <div>
                <div className="text-lg font-semibold text-gray-900">Edit Assignment Submission</div>
                <div className="text-sm text-gray-600">{editModal.subject?.subjectCode || ''} • {editModal.subject?.subjectName || ''}</div>
              </div>
              <button
                type="button"
                className="p-2 text-gray-500 hover:text-gray-700"
                onClick={closeSubmissionModal}
              >
                ✕
              </button>
            </div>

            <div className="px-4 py-4 space-y-3 text-sm text-gray-800">
              <div className="rounded-md bg-gray-50 border border-gray-200 p-3">
                <div className="font-semibold text-gray-900">{editModal.student?.fullName || '—'}</div>
                <div className="text-xs text-gray-600">ID: {editModal.student?.studentId || '—'} · Reg #: {editModal.student?.registrationNumber || '—'}</div>
              </div>

              <div className="grid grid-cols-1 gap-3 sm:grid-cols-4">
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-800">Submission Status</label>
                  <select
                    value={editModal.status}
                    onChange={(e) => setEditModal((prev) => ({ ...prev, status: e.target.value }))}
                    className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500"
                  >
                    {SUBMISSION_STATUS_OPTIONS.map((opt) => (
                      <option key={opt} value={opt}>{opt}</option>
                    ))}
                  </select>
                </div>

                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-800">1st Attempt Marks</label>
                  <input
                    type="number"
                    inputMode="decimal"
                    value={editModal.marks ?? ''}
                    onChange={(e) => setEditModal((prev) => ({ ...prev, marks: e.target.value }))}
                    className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500"
                    placeholder="Enter numeric mark"
                  />
                  <p className="text-xs text-gray-500">Leave blank to clear first-attempt marks.</p>
                </div>

                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-800">2nd Attempt Marks</label>
                  <input
                    type="number"
                    inputMode="decimal"
                    value={editModal.secondMarks ?? ''}
                    onChange={(e) => setEditModal((prev) => ({ ...prev, secondMarks: e.target.value }))}
                    className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500"
                    placeholder="Enter numeric mark"
                  />
                  <p className="text-xs text-gray-500">If first attempt is below 40, second attempt will display.</p>
                </div>

                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-800">Percentage Rate</label>
                  <input
                    type="number"
                    inputMode="decimal"
                    value={editModal.percentageRate ?? ''}
                    onChange={(e) => setEditModal((prev) => ({ ...prev, percentageRate: e.target.value }))}
                    className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500"
                    placeholder="Enter percentage (e.g. 65)"
                  />
                  <p className="text-xs text-gray-500">Optional: overall percentage rate for this assignment.</p>
                </div>
              </div>
            </div>

            <div className="flex items-center justify-end gap-2 border-t border-gray-200 bg-gray-50 px-4 py-3">
              <button
                type="button"
                className="rounded-md border border-gray-300 px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-white"
                onClick={closeSubmissionModal}
                disabled={savingSubmission}
              >
                Cancel
              </button>
              <button
                type="button"
                className="rounded-md bg-indigo-600 px-4 py-2 text-sm font-semibold text-white shadow hover:bg-indigo-700 disabled:opacity-60"
                onClick={handleSaveSubmission}
                disabled={savingSubmission}
              >
                {savingSubmission ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}
      </div>
    </DashboardLayout>
  );
}
